import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import {
  AlertCircle,
  Users,
  Download,
  Printer,
  RefreshCw,
  Search,
  Clock,
  Building2,
  MapPin,
  CheckCircle2,
  UserCheck,
  AlertTriangle,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';

/**
 * Emergency Roll Call
 * Shows live list of all people currently on site for emergency situations
 */
const EmergencyRollCall = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (user?.depot_id) {
      loadSessions();
    } else if (user !== undefined) {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Filter sessions based on search term
    if (searchTerm) {
      const filtered = sessions.filter(session =>
        session.person_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.person_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.work_area?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSessions(filtered);
    } else {
      setFilteredSessions(sessions);
    }
  }, [searchTerm, sessions]);

  useEffect(() => {
    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadSessions();
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadSessions = async () => {
    if (!user?.depot_id) return;
    
    setLoading(true);
    try {
      // Get all active sessions (not checked out yet) for this depot
      const { data, error } = await supabase
        .from('site_sessions')
        .select('*')
        .eq('depot_id', user.depot_id)
        .is('checked_out_at', null)
        .order('checked_in_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform data to match expected format (add check_in_time for compatibility)
      const transformedData = (data || []).map(session => ({
        ...session,
        check_in_time: session.checked_in_at,
      }));
      
      setSessions(transformedData);
      setFilteredSessions(transformedData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load on-site personnel',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExport = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roll-call-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: 'Export Complete',
      description: 'Roll call data has been exported to CSV.',
    });
  };

  const generateCSV = () => {
    const headers = ['Name', 'Type', 'Company', 'Work Area', 'Check-In Time', 'Phone', 'Emergency Contact'];
    const rows = filteredSessions.map(session => [
      session.person_name,
      session.person_type,
      session.person_company || 'N/A',
      session.work_area || 'N/A',
      new Date(session.check_in_time).toLocaleString(),
      session.person_phone || 'N/A',
      session.emergency_contact || 'N/A',
    ]);
    
    return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  };

  const generatePrintContent = () => {
    const now = new Date().toLocaleString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Emergency Roll Call - ${new Date().toLocaleDateString()}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              color: #E5B800;
            }
            .meta {
              margin-bottom: 20px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            .stats {
              display: flex;
              justify-content: space-around;
              margin-bottom: 20px;
            }
            .stat {
              text-align: center;
              padding: 10px;
              border: 1px solid #ddd;
              border-radius: 4px;
            }
            .stat-value {
              font-size: 24px;
              font-weight: bold;
              color: #E5B800;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>EMERGENCY ROLL CALL</h1>
            <p>On-Site Personnel Register</p>
          </div>
          
          <div class="meta">
            <p><strong>Generated:</strong> ${now}</p>
            <p><strong>Total On-Site:</strong> ${filteredSessions.length} people</p>
          </div>
          
          <div class="stats">
            <div class="stat">
              <div class="stat-value">${filteredSessions.filter(s => s.person_type === 'employee').length}</div>
              <div>Employees</div>
            </div>
            <div class="stat">
              <div class="stat-value">${filteredSessions.filter(s => s.person_type === 'contractor').length}</div>
              <div>Contractors</div>
            </div>
            <div class="stat">
              <div class="stat-value">${filteredSessions.filter(s => s.person_type === 'visitor').length}</div>
              <div>Visitors</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Type</th>
                <th>Company</th>
                <th>Work Area</th>
                <th>Check-In Time</th>
                <th>Phone</th>
                <th>Emergency Contact</th>
                <th>✓</th>
              </tr>
            </thead>
            <tbody>
              ${filteredSessions.map((session, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td><strong>${session.person_name}</strong></td>
                  <td>${session.person_type}</td>
                  <td>${session.person_company || 'N/A'}</td>
                  <td>${session.work_area || 'N/A'}</td>
                  <td>${new Date(session.check_in_time).toLocaleTimeString()}</td>
                  <td>${session.person_phone || 'N/A'}</td>
                  <td>${session.emergency_contact || 'N/A'}</td>
                  <td style="width: 30px;"></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div style="margin-top: 40px; border-top: 1px solid #ddd; padding-top: 20px;">
            <p><strong>Emergency Coordinator Signature:</strong> _______________________</p>
            <p><strong>Date & Time:</strong> _______________________</p>
          </div>
        </body>
      </html>
    `;
  };

  const getPersonTypeBadge = (type) => {
    const variants = {
      employee: { bg: 'bg-green-500', text: 'text-white' },
      contractor: { bg: 'bg-blue-500', text: 'text-white' },
      visitor: { bg: 'bg-purple-500', text: 'text-white' },
    };
    return variants[type] || { bg: 'bg-gray-500', text: 'text-white' };
  };

  const getTimeOnSite = (checkInTime) => {
    const now = new Date();
    const checkIn = new Date(checkInTime);
    const diffMs = now - checkIn;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins}m`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours}h ${mins}m`;
    }
  };

  const stats = {
    total: sessions.length,
    employees: sessions.filter(s => s.person_type === 'employee').length,
    contractors: sessions.filter(s => s.person_type === 'contractor').length,
    visitors: sessions.filter(s => s.person_type === 'visitor').length,
  };

  if (!user?.depot_id && !loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-foreground">No site assigned</p>
          <p className="mt-2 text-sm text-muted-foreground">Your account is not yet linked to a site. Contact your system administrator to be assigned to a depot.</p>
        </div>
      </div>
    );
  }

  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading roll call...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Emergency Header */}
      <div className="bg-red-500 text-white p-4 rounded-lg flex items-center gap-3">
        <AlertCircle className="h-6 w-6" />
        <div>
          <h1 className="text-2xl font-bold">Emergency Roll Call</h1>
          <p className="text-sm opacity-90">
            Live register of all personnel currently on site
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total On-Site</p>
                <p className="text-3xl font-bold text-red-500">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="text-3xl font-bold text-green-500">{stats.employees}</p>
              </div>
              <UserCheck className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Contractors</p>
                <p className="text-3xl font-bold text-blue-500">{stats.contractors}</p>
              </div>
              <Building2 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Visitors</p>
                <p className="text-3xl font-bold text-purple-500">{stats.visitors}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, company, or work area..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant={autoRefresh ? 'default' : 'outline'}
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Auto' : 'Manual'}
              </Button>
              
              <Button
                variant="outline"
                onClick={loadSessions}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              <Button
                variant="outline"
                onClick={handlePrint}
                className="flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Print
              </Button>

              <Button
                onClick={handleExport}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
            {autoRefresh && <Badge variant="outline" className="text-xs">Auto-refresh enabled</Badge>}
          </div>
        </CardContent>
      </Card>

      {/* Personnel List */}
      {filteredSessions.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No personnel on site</h3>
              <p className="text-muted-foreground mt-2">
                {searchTerm ? 'No results found for your search.' : 'The depot is currently empty.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Personnel Register ({filteredSessions.length})</span>
              {searchTerm && (
                <Badge variant="outline">
                  Filtered from {sessions.length} total
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 pb-2 border-b font-semibold text-sm">
                <div className="col-span-3">Name</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-2">Company</div>
                <div className="col-span-2">Work Area</div>
                <div className="col-span-2">Check-In</div>
                <div className="col-span-1">Duration</div>
                <div className="col-span-1">Contact</div>
              </div>

              {/* Table Rows */}
              {filteredSessions.map((session, index) => {
                const badge = getPersonTypeBadge(session.person_type);
                return (
                  <div
                    key={session.id}
                    className={`grid grid-cols-12 gap-4 py-3 border-b last:border-0 hover:bg-muted/50 transition-colors ${
                      index % 2 === 0 ? 'bg-muted/20' : ''
                    }`}
                  >
                    <div className="col-span-3 font-medium flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs">
                        {index + 1}
                      </span>
                      {session.person_name}
                    </div>
                    
                    <div className="col-span-1 flex items-center">
                      <Badge className={`${badge.bg} ${badge.text} text-xs`}>
                        {session.person_type}
                      </Badge>
                    </div>
                    
                    <div className="col-span-2 flex items-center text-sm text-muted-foreground">
                      {session.person_company || 'N/A'}
                    </div>
                    
                    <div className="col-span-2 flex items-center gap-1 text-sm">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {session.work_area || 'General'}
                    </div>
                    
                    <div className="col-span-2 flex items-center text-sm text-muted-foreground">
                      {new Date(session.check_in_time).toLocaleTimeString()}
                    </div>
                    
                    <div className="col-span-1 flex items-center">
                      <Badge variant="outline" className="text-xs">
                        {getTimeOnSite(session.check_in_time)}
                      </Badge>
                    </div>
                    
                    <div className="col-span-1 flex items-center text-sm text-muted-foreground">
                      {session.person_phone ? '✓' : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmergencyRollCall;
